import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { AdminLayout } from "./AdminLayout";

/**
 * Admin Content — Phase 6.1 (Control Center).
 *
 * Whitelisted editable homepage/footer copy stored in `site_settings`.
 * No arbitrary CSS. No new markup. Bounded-length string values only.
 */

const FIELDS = [
  { key: "hero_title",         label: "عنوان الهيرو الرئيسي",   type: "text",     max: 200 },
  { key: "hero_subtitle",      label: "النص الفرعي للهيرو",     type: "textarea", max: 500 },
  { key: "hero_cta_primary",   label: "زر النداء الأساسي",       type: "text",     max: 40  },
  { key: "hero_cta_secondary", label: "زر النداء الثانوي",       type: "text",     max: 40  },
  { key: "footer_tagline",     label: "شعار التذييل",            type: "text",     max: 200 },
  // Sprint 36 · homepage section copy
  { key: "section_kpi_title",             label: "عنوان قسم مؤشّرات الأداء",  type: "text",     max: 120 },
  { key: "section_kpi_description",       label: "وصف قسم مؤشّرات الأداء",   type: "textarea", max: 300 },
  { key: "section_opportunities_title",   label: "عنوان قسم الفرص",           type: "text",     max: 120 },
  { key: "section_opportunities_description", label: "وصف قسم الفرص",         type: "textarea", max: 300 },
  { key: "section_knowledge_title",       label: "عنوان قسم مكتبة المعرفة",  type: "text",     max: 120 },
  { key: "section_knowledge_description", label: "وصف قسم مكتبة المعرفة",   type: "textarea", max: 300 },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const readJson = async (r) => {
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

export default function AdminContentPage() {
  const [values, setValues] = useState({});
  const [initial, setInitial] = useState({});
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/settings`, { credentials: "include" });
      const data = await readJson(r);
      const shape = {};
      data.forEach((row) => { shape[row.key] = row.value || ""; });
      setValues(shape);
      setInitial(shape);
    } catch (e) {
      toast.error("تعذّر تحميل الإعدادات", { description: String(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setPending(key);
    try {
      const r = await fetch(`${API_BASE}/api/admin/settings/${key}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: values[key] ?? "" }),
      });
      await readJson(r);
      setInitial((s) => ({ ...s, [key]: values[key] }));
      toast.success("تم الحفظ", { description: key });
    } catch (e) {
      toast.error("تعذّر الحفظ", { description: String(e) });
    } finally {
      setPending(null);
    }
  };

  return (
    <AdminLayout>
      <section className="space-y-6">
        <header className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-fursa-700">
            Control Center · Content
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">
            محتوى الصفحة الرئيسية
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-ink-500">
            نصوص الواجهة العامة القابلة للتحرير — العنوان، الوصف، أزرار النداء والتذييل.
            جميع الحقول محدودة الطول ولا تسمح بأي HTML أو تنسيق مخصص.
          </p>
        </header>

        <div className="space-y-4">
          {FIELDS.map((f) => {
            const dirty = (values[f.key] ?? "") !== (initial[f.key] ?? "");
            const busy = pending === f.key;
            return (
              <div
                key={f.key}
                data-testid={`admin-content-field-${f.key}`}
                className="rounded-xl border border-surface-200 bg-white p-4 shadow-card"
              >
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-ink-900">{f.label}</label>
                  <span className="font-mono text-[10px] text-ink-500">{f.key}</span>
                </div>
                {f.type === "textarea" ? (
                  <textarea
                    value={values[f.key] ?? ""}
                    disabled={loading}
                    maxLength={f.max}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`admin-content-input-${f.key}`}
                    className="mt-2 min-h-24 w-full rounded-lg border border-surface-200 bg-white p-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
                  />
                ) : (
                  <input
                    type="text"
                    value={values[f.key] ?? ""}
                    disabled={loading}
                    maxLength={f.max}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`admin-content-input-${f.key}`}
                    className="mt-2 h-10 w-full rounded-lg border border-surface-200 bg-white px-3 text-sm outline-none hover:border-fursa-300 focus:border-fursa-500 focus:ring-2 focus:ring-fursa-500/15"
                  />
                )}
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-ink-500">
                    {(values[f.key] || "").length} / {f.max}
                  </span>
                  <button
                    type="button"
                    onClick={() => save(f.key)}
                    disabled={!dirty || busy}
                    data-testid={`admin-content-save-${f.key}`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-fursa-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {busy ? "جارٍ الحفظ…" : "حفظ"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </AdminLayout>
  );
}
